/**
 * ActivityLogModal — Salesforce-style activity timeline for one CRM account:
 * log Calls/Emails/Meetings/Tasks/Site Visits with a follow-up due date,
 * check them done, delete them.
 *
 * Module-level component with its OWN form state (the record editor's pattern), so
 * parent re-renders can't remount inputs mid-keystroke.
 */
import { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui/Modal';

const API = '/api/staffing';
const TYPES = ['Call', 'Email', 'Meeting', 'Task', 'Site Visit'];

const INPUT = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #D8DED4',
  background: '#fff',
  fontSize: 12.5,
  fontFamily: 'inherit',
  color: '#0D0D0D',
};
const LABEL = {
  display: 'block',
  marginBottom: 4,
  fontSize: 10,
  fontWeight: 800,
  color: '#666',
  letterSpacing: 1,
  textTransform: 'uppercase',
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y ? `${parseInt(m, 10)}/${parseInt(d, 10)}/${y.slice(2)}` : null;
};

export function ActivityLogModal({ record, recordType, recordLabel, myName, onClose, onChanged }) {
  const [activities, setActivities] = useState(null); // null = loading
  const [f, setF] = useState({ type: 'Call', subject: '', due_date: '', body: '' });
  const [err, setErr] = useState('');

  // FK column on staffing_activities this record hangs off (accounts only now
  // — the opportunities branch retired with the pipeline).
  const fk = 'account_id';

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/activities`);
      const all = await r.json();
      setActivities(all.filter((a) => a[fk] === record.id));
    } catch (e) {
      setErr(e.message);
      setActivities([]);
    }
  }, [record.id, fk]);

  useEffect(() => {
    load();
  }, [load]);

  const mutate = async (method, path, body) => {
    const res = await fetch(`${API}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
    return res.json();
  };

  const logActivity = async () => {
    if (!f.subject.trim()) {
      setErr('Subject is required');
      return;
    }
    setErr('');
    try {
      await mutate('POST', '/activities', {
        type: f.type,
        subject: f.subject.trim(),
        body: f.body || null,
        due_date: f.due_date || null,
        owner_name: myName || null,
        [fk]: record.id,
      });
      setF({ type: 'Call', subject: '', due_date: '', body: '' });
      await load();
      onChanged?.();
    } catch (e) {
      setErr(e.message);
    }
  };

  const toggleDone = async (a) => {
    try {
      await mutate('PUT', `/activities/${a.id}`, { done: !a.done });
      await load();
      onChanged?.();
    } catch (e) {
      setErr(e.message);
    }
  };

  const remove = async (a) => {
    try {
      await mutate('DELETE', `/activities/${a.id}`);
      await load();
      onChanged?.();
    } catch (e) {
      setErr(e.message);
    }
  };

  const today = todayISO();
  const open = (activities || []).filter((a) => !a.done);
  const done = (activities || []).filter((a) => a.done);

  const row = (a) => {
    const due = a.due_date ? String(a.due_date).slice(0, 10) : null;
    const overdue = !a.done && due && due < today;
    return (
      <div
        key={a.id}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '10px 12px',
          background: a.done ? '#FAFAFA' : overdue ? '#FFF5F5' : '#fff',
          border: `1px solid ${overdue ? '#F5C6C6' : '#EEE'}`,
          borderRadius: 8,
          marginBottom: 6,
        }}
      >
        <input
          type="checkbox"
          checked={!!a.done}
          onChange={() => toggleDone(a)}
          style={{ marginTop: 2, accentColor: '#1B5E20', cursor: 'pointer' }}
          aria-label="Mark done"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                color: '#1B5E20',
                background: '#F1F8E9',
                padding: '2px 8px',
                borderRadius: 999,
              }}
            >
              {a.type}
            </span>
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: a.done ? '#999' : '#0D0D0D',
                textDecoration: a.done ? 'line-through' : 'none',
              }}
            >
              {a.subject}
            </span>
          </div>
          {a.body && <div style={{ fontSize: 11.5, color: '#555', marginTop: 3 }}>{a.body}</div>}
          <div style={{ fontSize: 10.5, color: overdue ? '#B71C1C' : '#888', marginTop: 3, fontWeight: overdue ? 700 : 500 }}>
            {due ? `Due ${fmtDate(due)}${overdue ? ' · OVERDUE' : ''}` : 'No due date'}
            {a.owner_name ? ` · ${a.owner_name}` : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={() => remove(a)}
          title="Delete"
          aria-label={`Delete activity: ${a.subject}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B71C1C', fontSize: 14, lineHeight: 1, padding: 4 }}
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <Modal title={`Activities · ${recordLabel}`} onClose={onClose} width={620}>
      <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
        {/* Quick log */}
        <div style={{ background: '#F9FBF7', border: '1px solid #E5E9DD', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 130px', gap: 10 }}>
            <div>
              <label style={LABEL} htmlFor="act-type">
                Type
              </label>
              <select id="act-type" style={INPUT} value={f.type} onChange={(e) => setF((p) => ({ ...p, type: e.target.value }))}>
                {TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={LABEL} htmlFor="act-subject">
                Subject
              </label>
              <input
                id="act-subject"
                style={INPUT}
                value={f.subject}
                placeholder="e.g. Called Denise re: revised quote"
                onChange={(e) => setF((p) => ({ ...p, subject: e.target.value }))}
              />
            </div>
            <div>
              <label style={LABEL} htmlFor="act-due">
                Follow-up due
              </label>
              <input
                id="act-due"
                type="date"
                style={INPUT}
                value={f.due_date}
                onChange={(e) => setF((p) => ({ ...p, due_date: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <input
              style={INPUT}
              value={f.body}
              placeholder="Notes (optional)"
              onChange={(e) => setF((p) => ({ ...p, body: e.target.value }))}
            />
          </div>
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#B71C1C', fontWeight: 600 }}>{err}</span>
            <button
              type="button"
              onClick={logActivity}
              style={{
                padding: '8px 18px',
                borderRadius: 6,
                border: 'none',
                background: '#1B5E20',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Log Activity
            </button>
          </div>
        </div>

        {/* Timeline */}
        {activities === null ? (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: '#888' }}>Loading…</div>
        ) : activities.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: '#888' }}>
            No activity yet — log the first call, email or task above.
          </div>
        ) : (
          <>
            {open.length > 0 && (
              <div
                style={{ fontSize: 10, fontWeight: 800, color: '#666', letterSpacing: 1, textTransform: 'uppercase', margin: '4px 0 8px' }}
              >
                Open ({open.length})
              </div>
            )}
            {open.map(row)}
            {done.length > 0 && (
              <div
                style={{ fontSize: 10, fontWeight: 800, color: '#999', letterSpacing: 1, textTransform: 'uppercase', margin: '14px 0 8px' }}
              >
                Completed ({done.length})
              </div>
            )}
            {done.map(row)}
          </>
        )}
      </div>
    </Modal>
  );
}
